import {
  LessonPackResource,
  LessonPackResourceSchema,
  LinkedResource,
  LinkedResourceSchema,
  useCurriculum,
  useSubjectPlan,
  useUnitPlan,
} from "@twinkltech/curriculum-architect-domain-hooks";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

import { getUnitFromCell } from "@/utils/getUnitFromCell/getUnitFromCell";

import { LearningSequence } from "./LearningSequence";
import { LEARNING_SEQUENCE_NS } from "./LearningSequence.i18n";

export const LearningSequenceContainer = () => {
  const { t } = useTranslation(LEARNING_SEQUENCE_NS);
  const {
    cell_id: cellId = "generic",
    curriculum_id: curriculumId = "",
    scheme_id: schemeId = "",
    subject_id: subjectId = "",
  } = useParams();

  const {
    data: curriculum,
    error: curriculumError,
    isLoading: isLoadingCurriculum,
  } = useCurriculum({
    id: curriculumId,
  });

  const {
    data: unitPlan,
    error,
    error: unitPlanError,
    isLoading,
  } = useUnitPlan({ curriculumId, unitId: schemeId, subjectId });

  const {
    data: subjectPlan,
    error: subjectPlanError,
    isLoading: isLoadingSubjectPlan,
  } = useSubjectPlan({
    curriculumId,
    subjectId,
  });

  const params = {
    subjectId,
    schemeId,
  };

  const paramError =
    !schemeId || !subjectId
      ? new Error(
          t("errors.unit-plan-params-required", {
            params: JSON.stringify(params),
          })
        )
      : null;

  const isLessonPackResource = (
    resource: unknown
  ): resource is LessonPackResource => {
    return LessonPackResourceSchema.safeParse(resource).success;
  };

  const isLinkedResource = (resource: unknown): resource is LinkedResource => {
    return LinkedResourceSchema.safeParse(resource).success;
  };

  const lessonPacks: LessonPackResource[] =
    unitPlan?.resources.filter(isLessonPackResource) || [];

  const linkedResources: LinkedResource[] =
    unitPlan?.resources?.filter((resource) => isLinkedResource(resource)) || [];

  const unitInfo = getUnitFromCell(
    cellId,
    subjectPlan,
    unitPlan?.monolithUnitId
  );

  return (
    <LearningSequence
      curriculum={curriculum}
      description={unitPlan?.unitDescription}
      error={
        paramError ||
        error ||
        curriculumError ||
        subjectPlanError ||
        unitPlanError
      }
      isLoading={isLoading || isLoadingCurriculum || isLoadingSubjectPlan}
      keyArt={unitPlan?.keyArt}
      lessonPacks={lessonPacks}
      linkedResources={linkedResources}
      subject={unitPlan?.subject}
      subjectId={subjectId ?? ""}
      title={unitPlan?.unitTitle}
      type={unitPlan?.type ?? null}
      unitId={schemeId ?? ""}
      unitInfo={unitInfo || undefined}
      unitPackLink={unitPlan?.canonicalUrl ?? ""}
    />
  );
};
